import { useState } from "react";
import { usePuck } from "@measured/puck";
import {
  normalizePageAppearance,
  type PageAppearance,
} from "@/lib/cms-puck/blocks/appearance";
import { normalizePageCustomCss } from "@/lib/cms-puck/blocks/page-custom-css";
import {
  normalizePagePadding,
  type PagePadding,
} from "@/lib/cms-puck/blocks/page-padding";
import { PageAppearanceField } from "@/components/cms-puck/fields/PageAppearanceField";
import { PageAccessField } from "@/components/cms-puck/fields/PageAccessField";
import { PageCustomCssField } from "@/components/cms-puck/fields/PageCustomCssField";
import { PagePaddingField } from "@/components/cms-puck/fields/PagePaddingField";
import {
  normalizePuckAccessLevel,
  type PuckAccessLevel,
} from "@/lib/cms-puck/blocks/page-access";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabsContained";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { useSystemModel } from "@/context/cms-puck/SystemModelContext";

type RootTab = "appearance" | "access" | "css";

/**
 * Panneau latéral quand aucun bloc n'est sélectionné : réglages de la page
 * (apparence + CSS personnalisé ciblant les id/classes des blocs).
 */
export function RootFieldsPanel() {
  const cmsNew = useCmsNew();
  const { isSystemModel, kindLabel } = useSystemModel();
  const { appState, dispatch } = usePuck();
  const [tab, setTab] = useState<RootTab>("appearance");
  const rootProps = (appState.data.root?.props ?? {}) as Record<string, unknown>;
  const appearance = normalizePageAppearance(
    rootProps.appearance as PageAppearance | undefined,
  );
  const padding = normalizePagePadding(
    rootProps.padding as PagePadding | undefined,
  );
  const customCss = normalizePageCustomCss(rootProps.customCss);
  const accessLevel = normalizePuckAccessLevel(
    rootProps.accessLevel as PuckAccessLevel | undefined,
  );

  const setRootProps = (patch: Record<string, unknown>) => {
    dispatch({
      type: "setData",
      data: {
        ...appState.data,
        root: {
          ...appState.data.root,
          props: { ...rootProps, ...patch },
        },
      },
    });
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col overflow-x-hidden">
      <header className="min-w-0 shrink-0 border-b border-slate-200/80 bg-slate-50/60 px-6 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          {isSystemModel
            ? cmsNew("cms.new.systemModel.badge", "Modèle")
            : cmsNew("cms.new.auto.text.page", "Page")}
        </p>
        <h2 className="mt-1 break-words text-base font-semibold leading-tight text-slate-900">
          {isSystemModel
            ? cmsNew(
                "cms.new.systemModel.settingsTitle",
                "Réglages du modèle",
              )
            : cmsNew(
                "cms.new.auto.text.reglages_de_la_page",
                "Réglages de la page",
              )}
        </h2>
        <p className="mt-1 break-words text-xs text-slate-500">
          {isSystemModel
            ? kindLabel
              ? cmsNew(
                  "cms.new.systemModel.settingsDescKind",
                  "Apparence et CSS du modèle « {kind} » — appliqués partout où ce modèle est utilisé.",
                ).replace("{kind}", kindLabel)
              : cmsNew(
                  "cms.new.systemModel.settingsDesc",
                  "Apparence et CSS de ce modèle système — appliqués partout où il est utilisé.",
                )
            : cmsNew(
                "cms.new.auto.description.reglages_page_apparence_css",
                "Apparence globale et CSS personnalisé (ID / classes des blocs).",
              )}
        </p>
      </header>

      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as RootTab)}
        className="flex min-h-0 min-w-0 flex-1 flex-col"
      >
        <div className="min-w-0 shrink-0 px-6 pt-4">
          <TabsList className="grid h-10 w-full grid-cols-3 bg-slate-100/90">
            <TabsTrigger value="appearance" className="text-xs sm:text-sm">
              {cmsNew("cms.new.auto.text.apparence", "Apparence")}
            </TabsTrigger>
            <TabsTrigger value="access" className="text-xs sm:text-sm">
              {cmsNew("cms.new.auto.text.acces", "Accès")}
            </TabsTrigger>
            <TabsTrigger value="css" className="text-xs sm:text-sm">
              {cmsNew("cms.new.auto.text.css", "CSS")}
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-6 pb-6 pt-4">
          <TabsContent value="appearance" className="mt-0 min-w-0 space-y-4">
            <PageAppearanceField
              value={appearance}
              onChange={(next) => setRootProps({ appearance: next })}
            />
            <PagePaddingField
              value={padding}
              onChange={(next) => setRootProps({ padding: next })}
            />
          </TabsContent>
          <TabsContent value="access" className="mt-0 min-w-0 space-y-4">
            <PageAccessField
              value={accessLevel}
              onChange={(next) => setRootProps({ accessLevel: next })}
            />
          </TabsContent>
          <TabsContent value="css" className="mt-0 min-w-0 space-y-4">
            <PageCustomCssField
              value={customCss}
              onChange={(next) => setRootProps({ customCss: next })}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
